import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  Building2,
  CalendarClock,
  CheckCircle2,
  Clock3,
  FileText,
  Globe2,
  Loader2,
  LockKeyhole,
  MapPin,
  Package,
  Ship,
  Weight,
} from 'lucide-react';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { LOGO_URL } from '../constants/brand';
import { SCOPE_OPTIONS_BY_MODE } from '../config/chargeHeadsByModeLeg';
import { getApiBaseUrl, publicInquiryApi } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import { InquiryCreatorAvatar } from './InquiryCreatorIdentity';

const getAssetUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  const base = getApiBaseUrl().replace(/\/$/, '');
  return `${base}${String(value).startsWith('/') ? '' : '/'}${value}`;
};

const getPortLabel = (port, fallback) => {
  if (typeof port === 'string' && port.trim()) return port;
  if (port && typeof port === 'object') {
    return port.name || port.code || port.city || fallback || 'Not specified';
  }
  return fallback || 'Not specified';
};

const getPortDisplay = (name, code) => {
  const portName = typeof name === 'string' ? name.trim() : '';
  const portCode = typeof code === 'string' ? code.trim() : '';
  if (!portName) return portCode || 'Not specified';
  if (!portCode || portName.includes(portCode)) return portName;
  return `${portName} (${portCode})`;
};

const getScopeOptions = (quoteType) => {
  const mode = {
    'Ocean FCL': 'ocean-fcl',
    'Ocean LCL': 'ocean-lcl',
    Air: 'air',
    Transport: 'transport',
    Warehousing: 'warehousing',
    Breakbulk: 'breakbulk',
  }[quoteType];
  return (mode && SCOPE_OPTIONS_BY_MODE[mode]) || [];
};

const getContainerValues = (inquiry, formData, type) => {
  const containers = formData.containers || inquiry.containers;
  const entry = Array.isArray(containers)
    ? containers.find((container) => container?.container_type === type)
    : null;
  const quantity = Array.isArray(containers)
    ? entry?.quantity
    : containers?.[type];
  const weight = formData.containerWeights?.[type] ?? entry?.weight_kg;
  return { quantity: Number(quantity) || 0, weight };
};

const formatNumber = (value, maximumFractionDigits = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric.toLocaleString(undefined, { maximumFractionDigits });
};

const getCountdownParts = (deadline, nowMs) => {
  const deadlineMs = new Date(deadline).getTime();
  if (!Number.isFinite(deadlineMs)) return null;
  const remaining = Math.max(0, deadlineMs - nowMs);
  const totalSeconds = Math.floor(remaining / 1000);
  return {
    expired: remaining <= 0,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
};

const CountdownUnit = ({ value, label }) => (
  <div className="min-w-[4.5rem] border-r border-white/20 px-3 text-center last:border-r-0">
    <div className="text-2xl font-bold tabular-nums md:text-3xl">
      {String(value).padStart(2, '0')}
    </div>
    <div className="mt-1 text-[11px] font-medium uppercase text-blue-100">{label}</div>
  </div>
);

const PublicInquiry = () => {
  const { shareToken } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    const loadInquiry = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await publicInquiryApi.getInquiry(shareToken);
        if (cancelled) return;
        setPayload(response.data);
        const serverTime = new Date(response.data?.server_time).getTime();
        setServerOffsetMs(Number.isFinite(serverTime) ? serverTime - Date.now() : 0);
      } catch (requestError) {
        if (!cancelled) {
          setError(
            getErrorMessage(
              requestError.response?.data?.detail,
              'This public inquiry link is unavailable.'
            )
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadInquiry();
    return () => {
      cancelled = true;
    };
  }, [shareToken]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    document.title = payload?.inquiry?.reference_number
      ? `${payload.inquiry.reference_number} | One Globe Alliance`
      : 'Public Freight Inquiry | One Globe Alliance';
  }, [payload?.inquiry?.reference_number]);

  const inquiry = payload?.inquiry || {};
  const owner = payload?.owner || {};
  const countdown = useMemo(
    () => getCountdownParts(inquiry.response_deadline, nowMs + serverOffsetMs),
    [inquiry.response_deadline, nowMs, serverOffsetMs]
  );
  const isExpired = payload?.is_expired || countdown?.expired;
  const returnTo = `/quotes?inquiry=${encodeURIComponent(inquiry.quote_id || '')}`;
  const loginHref = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  const registerHref = `/register?returnTo=${encodeURIComponent(returnTo)}`;
  const origin = getPortLabel(
    inquiry.origin_port,
    inquiry.origin_city || inquiry.form_data?.polName || inquiry.form_data?.pol
  );
  const destination = getPortLabel(
    inquiry.destination_port,
    inquiry.destination_city || inquiry.form_data?.podName || inquiry.form_data?.pod
  );
  const formData = inquiry.form_data || {};
  const selectedScope = Array.isArray(formData.scope)
    ? formData.scope
    : formData.scope
      ? [formData.scope]
      : [];
  const scopeOptions = getScopeOptions(inquiry.quote_type);
  const scope = selectedScope.join(', ');
  const isOcean = inquiry.quote_type === 'Ocean FCL' || inquiry.quote_type === 'Ocean LCL';
  const isOceanFcl = inquiry.quote_type === 'Ocean FCL';
  const standardContainers = isOceanFcl
    ? ['20GP', '20HC', '40GP', '40HC', '45HC']
        .map((type) => ({ type, ...getContainerValues(inquiry, formData, type) }))
        .filter(({ quantity }) => quantity > 0)
    : [];
  const specialContainers = Array.isArray(formData.oogContainers) ? formData.oogContainers : [];
  const reefer = formData.reefer || {};
  const hasReeferDetails = [reefer['20RF'], reefer['40RF'], formData.tempRange20, formData.tempRange40]
    .some((value) => value !== undefined && value !== null && value !== '');
  const portDetails = [
    formData.por && {
      label: 'POR (Place of Receipt)',
      value: getPortDisplay(formData.porName, formData.por),
      country: formData.porCountry,
    },
    {
      label: 'POL (Port of Loading)',
      value: getPortDisplay(
        inquiry.origin_port?.name || formData.polName,
        inquiry.origin_port?.code || formData.pol
      ),
      country: formData.polCountry,
    },
    {
      label: 'POD (Port of Discharge)',
      value: getPortDisplay(
        inquiry.destination_port?.name || formData.podName,
        inquiry.destination_port?.code || formData.pod
      ),
      country: formData.podCountry,
    },
    formData.fpod && {
      label: 'FPOD (Final Place of Delivery)',
      value: getPortDisplay(formData.fpodName, formData.fpod),
      country: formData.fpodCountry,
    },
  ].filter(Boolean);
  const shipmentTerms = [
    { label: 'Incoterm', value: inquiry.incoterm || formData.incoterm },
    { label: 'POL Free Time (days)', value: formData.polFreeTime },
    { label: 'POD Free Time (days)', value: formData.podFreeTime },
    { label: 'BL Type', value: formData.blType },
    { label: 'BL Format', value: formData.blFormat },
  ].filter(({ value }) => value !== undefined && value !== null && value !== '');

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center text-slate-600">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-3">Loading public inquiry...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto h-10 w-10 text-red-600" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Inquiry link unavailable</h1>
          <p className="mt-2 text-slate-600">{error}</p>
          <Button asChild className="mt-6 bg-blue-600 text-white hover:bg-blue-700">
            <Link to="/login">Go to One Globe Alliance</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="One Globe Alliance" className="h-12 w-auto object-contain" />
            <div className="hidden sm:block">
              <p className="font-bold">One Globe Alliance</p>
              <p className="text-xs text-slate-500">Public Freight Inquiry</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to={loginHref}>Login</Link>
            </Button>
            <Button asChild className="bg-blue-600 text-white hover:bg-blue-700">
              <Link to={registerHref}>Register</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className={isExpired ? 'bg-slate-800 text-white' : 'bg-blue-700 text-white'}>
          <div className="mx-auto max-w-6xl px-4 py-8 md:px-8 md:py-10">
            {isExpired ? (
              <div className="flex flex-col gap-3">
                <Badge className="w-fit bg-red-100 text-red-800 hover:bg-red-100">
                  Quotation closed
                </Badge>
                <h1 className="text-3xl font-bold">This inquiry has ended.</h1>
                <p className="text-slate-200">
                  The deadline for submitting a quotation has passed.
                </p>
              </div>
            ) : (
              <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
                <div>
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                    Accepting quotations
                  </Badge>
                  <h1 className="mt-3 text-2xl font-bold md:text-3xl">
                    Quotation deadline countdown
                  </h1>
                  <p className="mt-2 text-blue-100">
                    Submit before {new Date(inquiry.response_deadline).toLocaleString([], {
                      dateStyle: 'long',
                      timeStyle: 'short',
                    })}
                  </p>
                </div>
                {countdown && (
                  <div className="flex w-fit items-stretch border border-white/30 bg-blue-800/50 py-3">
                    <CountdownUnit value={countdown.days} label="Days" />
                    <CountdownUnit value={countdown.hours} label="Hours" />
                    <CountdownUnit value={countdown.minutes} label="Minutes" />
                    <CountdownUnit value={countdown.seconds} label="Seconds" />
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="border-b bg-white">
          <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
            <div className="flex flex-col justify-between gap-5 md:flex-row md:items-start">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{inquiry.quote_type || 'Freight Inquiry'}</Badge>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                    {inquiry.status}
                  </Badge>
                </div>
                <h2 className="mt-3 text-3xl font-bold">
                  {inquiry.reference_number || 'Freight inquiry'}
                </h2>
                <p className="mt-2 text-lg text-slate-600">{inquiry.commodity || 'General cargo'}</p>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <LockKeyhole size={16} />
                Read-only public view
              </div>
            </div>
          </div>
        </section>

        <section className="border-b bg-slate-50">
          <div className="mx-auto grid max-w-6xl gap-0 px-4 py-8 md:grid-cols-2 md:px-8">
            <div className="border-b border-slate-200 py-5 md:border-b-0 md:border-r md:pr-8">
              <p className="text-xs font-semibold uppercase text-slate-500">Origin</p>
              <p className="mt-2 flex items-center gap-2 text-lg font-semibold">
                <MapPin className="text-emerald-600" size={19} />
                {origin}
              </p>
            </div>
            <div className="py-5 md:pl-8">
              <p className="text-xs font-semibold uppercase text-slate-500">Destination</p>
              <p className="mt-2 flex items-center gap-2 text-lg font-semibold">
                <MapPin className="text-red-600" size={19} />
                {destination}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-6xl px-4 py-10 md:px-8">
            <h2 className="text-xl font-bold">Inquiry details</h2>
            <div className="mt-6 grid gap-x-10 gap-y-6 border-y border-slate-200 py-7 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Package className="text-blue-600" size={20} />
                <p className="mt-2 text-xs uppercase text-slate-500">Commodity</p>
                <p className="mt-1 font-semibold">{inquiry.commodity || 'Not specified'}</p>
              </div>
              <div>
                <Ship className="text-blue-600" size={20} />
                <p className="mt-2 text-xs uppercase text-slate-500">Service scope</p>
                <p className="mt-1 font-semibold">{scope || inquiry.quote_type || 'Not specified'}</p>
              </div>
              <div>
                <Weight className="text-blue-600" size={20} />
                <p className="mt-2 text-xs uppercase text-slate-500">Weight</p>
                <p className="mt-1 font-semibold">
                  {formatNumber(inquiry.weight_kg) ? `${formatNumber(inquiry.weight_kg)} kg` : 'Not specified'}
                </p>
              </div>
              <div>
                <CalendarClock className="text-blue-600" size={20} />
                <p className="mt-2 text-xs uppercase text-slate-500">Deadline to quote</p>
                <p className="mt-1 font-semibold">
                  {new Date(inquiry.response_deadline).toLocaleString([], {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-5">
              {isOcean && (scopeOptions.length > 0 || selectedScope.length > 0) && (
                <section className="border border-slate-200 bg-slate-50 p-5">
                  <h3 className="font-semibold">Scope (Select all that apply)</h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(scopeOptions.length > 0 ? scopeOptions : selectedScope).map((item) => {
                      const selected = selectedScope.includes(item);
                      return (
                        <span
                          key={item}
                          className={selected
                            ? 'inline-flex items-center bg-blue-100 px-3 py-1 text-sm text-blue-800'
                            : 'inline-flex items-center bg-white px-3 py-1 text-sm text-slate-500'}
                        >
                          {selected ? '✓' : '○'} {item}
                        </span>
                      );
                    })}
                  </div>
                </section>
              )}

              {isOcean && (
                <section className="border border-slate-200 bg-slate-50 p-5">
                  <h3 className="flex items-center gap-2 font-semibold">
                    <MapPin size={17} className="text-slate-500" />
                    Port details
                  </h3>
                  <div className="mt-4 grid gap-5 text-sm sm:grid-cols-2">
                    {portDetails.map((port) => (
                      <div key={port.label}>
                        <p className="text-xs text-slate-500">{port.label}</p>
                        <p className="mt-1 font-medium">
                          {port.value}
                          {port.country ? ` — ${port.country}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                  {shipmentTerms.length > 0 && (
                    <div className="mt-5 grid gap-5 border-t border-slate-200 pt-5 text-sm sm:grid-cols-2 lg:grid-cols-4">
                      {shipmentTerms.map((term) => (
                        <div key={term.label}>
                          <p className="text-xs text-slate-500">{term.label}</p>
                          <p className="mt-1 font-medium">
                            {Array.isArray(term.value) ? term.value.join(', ') : term.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {isOceanFcl && standardContainers.length > 0 && (
                <section className="border border-slate-200 bg-slate-50 p-5">
                  <h3 className="flex items-center gap-2 font-semibold">
                    <Ship size={17} className="text-slate-500" />
                    Standard containers
                  </h3>
                  <div className="mt-4 grid gap-5 text-sm sm:grid-cols-3 lg:grid-cols-5">
                    {standardContainers.map((container) => (
                      <div key={container.type}>
                        <p className="text-xs text-slate-500">{container.type}</p>
                        <p className="mt-1 font-medium">{container.quantity}</p>
                        {container.weight !== undefined && container.weight !== null && container.weight !== '' && (
                          <p className="mt-1 text-slate-600">{container.weight} kg</p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {isOceanFcl && specialContainers.length > 0 && (
                <section className="border border-slate-200 bg-slate-50 p-5">
                  <h3 className="font-semibold">Special containers</h3>
                  <div className="mt-4 space-y-4">
                    {specialContainers.map((container, index) => {
                      const count = specialContainers
                        .slice(0, index + 1)
                        .filter((item) => item.type === container.type).length;
                      return (
                        <div
                          key={`${container.type || 'container'}-${index}`}
                          className="border border-amber-200 bg-amber-50 p-4 text-sm"
                        >
                          <p className="border-b border-amber-200 pb-3 font-medium">
                            {container.type || 'Special'} Container
                            <span className="ml-2 text-xs font-normal text-slate-600">Count-{count}</span>
                          </p>
                          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <div>
                              <p className="text-xs text-slate-500">Gauge Type</p>
                              <p className="mt-1">{container.gauge === 'out' ? 'Out-Gauge' : 'In-Gauge'}</p>
                            </div>
                            {container.gauge === 'out' && (
                              <>
                                <div>
                                  <p className="text-xs text-slate-500">Dimensions</p>
                                  <p className="mt-1">
                                    {[container.length, container.width, container.height]
                                      .filter((value) => value !== undefined && value !== null && value !== '')
                                      .join(' x ') || '-'} {container.unit || ''}
                                  </p>
                                </div>
                              </>
                            )}
                            <div>
                              <p className="text-xs text-slate-500">Cargo Weight (kg)</p>
                              <p className="mt-1">{container.weight ?? '-'}</p>
                            </div>
                            <div className="sm:col-span-2">
                              <p className="text-xs text-slate-500">Cargo Description</p>
                              <p className="mt-1 whitespace-pre-wrap">{container.cargoDescription || '-'}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {isOceanFcl && hasReeferDetails && (
                <section className="border border-slate-200 bg-slate-50 p-5">
                  <h3 className="font-semibold">Reefer &amp; Special Cargo</h3>
                  <div className="mt-4 grid gap-5 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <p className="text-xs text-slate-500">20' Reefer</p>
                      <p className="mt-1 font-medium">{reefer['20RF'] ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Temp Range (20')</p>
                      <p className="mt-1 font-medium">{formData.tempRange20 || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">40' Reefer</p>
                      <p className="mt-1 font-medium">{reefer['40RF'] ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Temp Range (40')</p>
                      <p className="mt-1 font-medium">{formData.tempRange40 || '-'}</p>
                    </div>
                  </div>
                </section>
              )}
            </div>

            <div className="mt-8 grid gap-8 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <h3 className="font-semibold">Cargo description</h3>
                <p className="mt-2 whitespace-pre-wrap text-slate-600">
                  {inquiry.cargo_description || 'No additional cargo description provided.'}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {inquiry.hs_code && <Badge variant="outline">HS code: {inquiry.hs_code}</Badge>}
                  {inquiry.incoterm && !isOcean && (
                    <Badge variant="outline">Incoterm: {inquiry.incoterm}</Badge>
                  )}
                  {formatNumber(inquiry.volume_cbm) && (
                    <Badge variant="outline">Volume: {formatNumber(inquiry.volume_cbm)} CBM</Badge>
                  )}
                  {inquiry.is_hazardous && (
                    <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                      Hazardous cargo {inquiry.hazardous_class ? `Class ${inquiry.hazardous_class}` : ''}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="border-l-0 border-slate-200 lg:border-l lg:pl-8">
                <h3 className="font-semibold">Supporting documents</h3>
                {Array.isArray(inquiry.attachments) && inquiry.attachments.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {inquiry.attachments.map((file, index) => (
                      <a
                        key={`${file.url || file.file_url}-${index}`}
                        href={getAssetUrl(file.url || file.file_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 border border-slate-200 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
                      >
                        <FileText size={16} />
                        <span className="truncate">
                          {file.original_filename || file.filename || `Document ${index + 1}`}
                        </span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">No public documents attached.</p>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y bg-slate-50">
          <div className="mx-auto max-w-6xl px-4 py-9 md:px-8">
            <p className="text-xs font-semibold uppercase text-slate-500">Inquiry shared by</p>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex shrink-0 items-center gap-3">
                <InquiryCreatorAvatar
                  name={owner.name}
                  profilePicture={owner.profile_picture}
                  size="lg"
                />
                <div
                  className="flex h-16 w-16 items-center justify-center overflow-hidden border bg-white"
                  aria-label={`${owner.company_name || 'Company'} logo`}
                >
                  {owner.company_logo ? (
                    <img
                      src={getAssetUrl(owner.company_logo)}
                      alt={`${owner.company_name} logo`}
                      className="h-full w-full object-contain p-1"
                    />
                  ) : (
                    <Building2 className="text-slate-400" size={30} />
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-slate-500">Created by</p>
                <p className="mt-1 text-lg font-bold">{owner.name}</p>
                <p className="mt-1 font-medium text-slate-700">{owner.company_name}</p>
                {(owner.city || owner.country) && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                    <MapPin size={14} />
                    {[owner.city, owner.country].filter(Boolean).join(', ')}
                  </p>
                )}
                {owner.website && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                    <Globe2 size={14} />
                    {owner.website}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-6xl px-4 py-12 md:px-8">
            <div className="border border-blue-200 bg-blue-50 px-6 py-8 md:flex md:items-center md:justify-between md:gap-8">
              <div>
                {isExpired ? (
                  <Clock3 className="text-slate-600" size={28} />
                ) : (
                  <CheckCircle2 className="text-emerald-600" size={28} />
                )}
                <h2 className="mt-3 text-2xl font-bold">
                  Register/Login on One Globe Alliance to Quote
                </h2>
                <p className="mt-2 max-w-2xl text-slate-600">
                  {isExpired
                    ? 'This inquiry is closed, but you can still join the network and view other active freight opportunities.'
                    : 'Join the One Globe Alliance network, return to this inquiry, and submit your quotation before the deadline.'}
                </p>
              </div>
              <div className="mt-6 flex shrink-0 flex-wrap gap-3 md:mt-0">
                <Button asChild className="bg-blue-600 text-white hover:bg-blue-700">
                  <Link to={loginHref}>Login Now</Link>
                </Button>
                <Button asChild variant="outline" className="border-blue-300 bg-white">
                  <Link to={registerHref}>Register</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t bg-slate-900 py-6 text-center text-sm text-slate-300">
        One Globe Alliance freight-forwarding network
      </footer>
    </div>
  );
};

export default PublicInquiry;
